// SignOffCard — one HumanApproval rendered as the bento receipt card.
// Per STYLE_GUIDE.md §Sign-off receipts: headline → body (summary +
// fields + preview_text + image attachments) → Approve / Deny buttons
// → optional deny-reason flow.

import { useState } from "react"
import { useTranslations } from "../../lib/translations"
import { useStore } from "../../stores/store"
import { approvalsStore, decideApproval } from "../../stores/approvalsStore"

export function SignOffCard({ approval }) {
  const t = useTranslations()
  const deciding = useStore(approvalsStore, (s) => s.decidingId === approval.id)
  const error    = useStore(approvalsStore, (s) => s.decideError)
  const [denyOpen, setDenyOpen] = useState(false)
  const [denyReason, setDenyReason] = useState("")

  const approve = () => decideApproval(approval, "approved").catch(() => {})
  const deny    = () => {
    decideApproval(approval, "denied", denyReason || null)
      .then(() => { setDenyOpen(false); setDenyReason("") })
      .catch(() => {})
  }

  const receipt = approval.receipt || {}
  const fields = Array.isArray(receipt.fields) ? receipt.fields : []
  const images = (approval.attachments || []).filter((a) => /^image\//.test(a.content_type || ""))
  const other  = (approval.attachments || []).filter((a) => !/^image\//.test(a.content_type || ""))

  return (
    <div className="cm-signoff">
      <div className="cm-signoff-head">
        <div className="cm-signoff-headline">
          {receipt.headline || `${approval.agent_task.kind} — pending`}
        </div>
        <div className="cm-signoff-meta">
          {approval.approver_role && (
            <span className="cm-signoff-meta-pill">{t("signoff.approver_role", { role: approval.approver_role })}</span>
          )}
          <span className="cm-signoff-meta-pill">{t("signoff.expires_in", { relative: relTime(approval.expires_at) })}</span>
        </div>
      </div>

      {receipt.summary && <div className="cm-signoff-summary">{receipt.summary}</div>}

      {fields.length > 0 && (
        <div className="cm-signoff-fields">
          {fields.map((f, i) => (
            <div className="cm-signoff-field" key={i}>
              <span className="cm-signoff-field-label">{f.label}</span>
              <span className={f.mono ? "cm-signoff-field-value mono" : "cm-signoff-field-value"}>{f.value}</span>
            </div>
          ))}
        </div>
      )}

      {receipt.preview_text && (
        <pre className="cm-signoff-preview">{receipt.preview_text}</pre>
      )}

      {images.length > 0 && (
        <div className="cm-signoff-images">
          {images.map((att) => (
            <a className="cm-signoff-image" key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
              <img src={att.url} alt={att.filename} />
              <span className="cm-signoff-image-name">{att.filename}</span>
            </a>
          ))}
        </div>
      )}

      {other.length > 0 && (
        <div className="cm-signoff-attachments">
          {other.map((att) => (
            <a className="cm-signoff-attachment" key={att.id} href={att.url} target="_blank" rel="noopener noreferrer">
              {att.filename}
            </a>
          ))}
        </div>
      )}

      <div className="cm-signoff-actions">
        {!denyOpen && (
          <>
            <button className="cm-btn-deny" onClick={() => setDenyOpen(true)} disabled={deciding}>
              {t("signoff.deny")}
            </button>
            <button className="cm-btn-approve" onClick={approve} disabled={deciding}>
              {deciding ? t("signoff.deciding") : t("signoff.approve")}
            </button>
          </>
        )}
        {denyOpen && (
          <div className="cm-signoff-deny">
            <input
              type="text"
              placeholder={t("signoff.deny_form.reason_label")}
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              disabled={deciding}
              autoFocus
            />
            <button className="cm-btn-cancel" onClick={() => setDenyOpen(false)} disabled={deciding}>
              {t("signoff.deny_form.cancel")}
            </button>
            <button className="cm-btn-deny" onClick={deny} disabled={deciding}>
              {deciding ? t("signoff.deciding") : t("signoff.deny_form.confirm")}
            </button>
          </div>
        )}
      </div>

      {error && <div className="cm-signoff-error">{t("signoff.error")}</div>}
    </div>
  )
}

function relTime(iso) {
  if (!iso) return "—"
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return "now"
  const h = Math.floor(ms / 3_600_000)
  const m = Math.floor((ms % 3_600_000) / 60_000)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}
